/**
 * RuleBuilder - Reusable component for building file selection rules
 *
 * Allows users to add, configure, and remove rules with logical operators.
 * Supports mutual exclusivity between file-based rules and view-based source.
 */

import React, { useState } from 'react';
import { Plus, Trash2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ViewRule, RuleType, RuleConfig } from '@/types/views';
import { RuleConfigFields } from './RuleConfigFields';
import { FILE_BASED_RULE_TYPES } from './fileBasedRuleTypes';
import {
  SourceTypeSwitchConfirmDialog,
  type SwitchDirection,
} from './SourceTypeSwitchConfirmDialog';

interface RuleBuilderProps {
  rules: ViewRule[];
  onRulesChange: (rules: ViewRule[]) => void;
  ruleLogic?: 'AND' | 'OR';
  onRuleLogicChange?: (logic: 'AND' | 'OR') => void;
  /** Called when source type is switched (file <-> view) after user confirms */
  onSourceTypeChange?: () => void;
  /** Called when the selected source view changes within view mode */
  onSourceViewChange?: () => void;
  /** View names to exclude from ViewSelector (e.g. to prevent self-reference) */
  excludeViewNames?: string[];
}

/**
 * Check if any rule is a view_name rule
 */
const hasViewNameRule = (rules: ViewRule[]): boolean =>
  rules.some((rule) => rule.type === 'view_name');

/**
 * Check if any rule is a file-based rule (not view_name)
 */
const hasFileBasedRules = (rules: ViewRule[]): boolean =>
  rules.some((rule) => rule.type !== 'view_name');

/**
 * Create default config for a rule type
 */
const createDefaultConfig = (type: RuleType): RuleConfig => {
  switch (type) {
    case 'path_pattern':
      return { type: 'path_pattern', patterns: [], exclude_patterns: [], case_sensitive: false };
    case 'source_id':
      return { type: 'source_id', source_ids: [] };
    case 'dataset_id':
      return { type: 'dataset_id', dataset_ids: [] };
    case 'dataset_name':
      return { type: 'dataset_name', dataset_names: [] };
    case 'metadata':
      return { type: 'metadata', field_path: '', operator: 'equals', value: '' };
    case 'date_range':
      return { type: 'date_range', date_field: 'created_at' };
    case 'file_size':
      return { type: 'file_size' };
    case 'tag':
      return { type: 'tag', tags: [], match_all: false };
    case 'view_name':
      return { type: 'view_name', view_name: '' };
  }
};

export const RuleBuilder: React.FC<RuleBuilderProps> = ({
  rules,
  onRulesChange,
  ruleLogic = 'AND',
  onRuleLogicChange,
  onSourceTypeChange,
  onSourceViewChange,
  excludeViewNames,
}) => {
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    direction: SwitchDirection;
  }>({ open: false, direction: 'to-view' });

  const isViewMode = hasViewNameRule(rules);
  const isFileMode = hasFileBasedRules(rules);

  /**
   * Add a new file-based rule
   */
  const handleAddRule = () => {
    const newRule: ViewRule = {
      id: `rule-${Date.now()}-${Math.random()}`, // nosemgrep: rules.lgpl.nodejs_scan.javascript-crypto-rule-node_insecure_random_generator  -- opaque client-side rule id (React key), not a security value
      type: 'path_pattern',
      config: createDefaultConfig('path_pattern'),
    };
    onRulesChange([...rules, newRule]);
  };

  /**
   * Switch to view-based source (clears existing rules)
   */
  const handleSwitchToView = () => {
    if (isFileMode) {
      setConfirmDialog({ open: true, direction: 'to-view' });
    } else {
      applyViewSwitch();
    }
  };

  /**
   * Switch to file-based rules (clears existing view rule)
   */
  const handleSwitchToFile = () => {
    if (isViewMode) {
      setConfirmDialog({ open: true, direction: 'to-file' });
    } else {
      handleAddRule();
    }
  };

  /**
   * Apply the switch to view-based source
   */
  const applyViewSwitch = () => {
    const viewRule: ViewRule = {
      id: `rule-${Date.now()}-${Math.random()}`, // nosemgrep: rules.lgpl.nodejs_scan.javascript-crypto-rule-node_insecure_random_generator  -- opaque client-side rule id (React key), not a security value
      type: 'view_name',
      config: createDefaultConfig('view_name'),
    };
    onRulesChange([viewRule]);
  };

  /**
   * Handle confirmation dialog result
   */
  const handleConfirm = () => {
    if (confirmDialog.direction === 'to-view') {
      applyViewSwitch();
    } else {
      // Switch to file mode: clear rules and add a default file-based rule
      const newRule: ViewRule = {
        id: `rule-${Date.now()}-${Math.random()}`, // nosemgrep: rules.lgpl.nodejs_scan.javascript-crypto-rule-node_insecure_random_generator  -- opaque client-side rule id (React key), not a security value
        type: 'path_pattern',
        config: createDefaultConfig('path_pattern'),
      };
      onRulesChange([newRule]);
    }
    // Notify parent to clear column configurations
    onSourceTypeChange?.();
    setConfirmDialog({ open: false, direction: confirmDialog.direction });
  };

  const handleCancelConfirm = () => {
    setConfirmDialog({ open: false, direction: confirmDialog.direction });
  };

  /**
   * Remove a rule
   */
  const handleRemoveRule = (ruleId: string) => {
    onRulesChange(rules.filter((rule) => rule.id !== ruleId));
  };

  /**
   * Update rule type
   */
  const handleRuleTypeChange = (ruleId: string, newType: RuleType) => {
    onRulesChange(
      rules.map((rule) =>
        rule.id === ruleId
          ? { ...rule, type: newType, config: createDefaultConfig(newType) }
          : rule
      )
    );
  };

  /**
   * Update rule config
   */
  const handleConfigChange = (ruleId: string, config: RuleConfig) => {
    onRulesChange(
      rules.map((rule) =>
        rule.id === ruleId ? { ...rule, config } : rule
      )
    );
  };

  return (
    <div className="space-y-4">
      {/* Confirmation Dialog */}
      <SourceTypeSwitchConfirmDialog
        open={confirmDialog.open}
        direction={confirmDialog.direction}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
      />

      {/* Rule Logic Selector - hidden when in view mode */}
      {!isViewMode && rules.length > 1 && onRuleLogicChange && (
        <div className="flex items-center gap-2 mb-4">
          <Label>Combine rules with:</Label>
          <Select value={ruleLogic} onValueChange={(value: 'AND' | 'OR') => onRuleLogicChange(value)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AND">AND</SelectItem>
              <SelectItem value="OR">OR</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground ml-2">
            {ruleLogic === 'AND' ? 'Files must match ALL rules' : 'Files must match ANY rule'}
          </p>
        </div>
      )}

      {/* Rules List */}
      {rules.map((rule, index) => (
        <Card key={rule.id} className="relative">
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Rule Header */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-4">
                  {/* Rule Type Selector - show label for view_name, full selector for file-based */}
                  {rule.type === 'view_name' ? (
                    <div>
                      <Label>Source Type</Label>
                      <div className="flex items-center gap-2 mt-1 px-3 py-2 border rounded-md bg-muted/50">
                        <Eye className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Build From View</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Label>Rule Type</Label>
                      <Select
                        value={rule.type}
                        onValueChange={(value: RuleType) => handleRuleTypeChange(rule.id, value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FILE_BASED_RULE_TYPES.map(({ value, label }) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Rule Configuration Fields */}
                  <RuleConfigFields
                    ruleType={rule.type}
                    config={rule.config}
                    onConfigChange={(config) => handleConfigChange(rule.id, config)}
                    excludeViewNames={excludeViewNames}
                    onSourceViewChange={onSourceViewChange}
                  />
                </div>

                {/* Remove Button */}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveRule(rule.id)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Logic Operator Between Rules - hidden in view mode */}
              {!isViewMode && index < rules.length - 1 && (
                <div className="flex justify-center py-2">
                  <div className="px-3 py-1 bg-muted rounded-md text-sm font-medium">
                    {ruleLogic}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Action Buttons - contextual based on current mode */}
      {rules.length > 0 && !isViewMode && (
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={handleAddRule} className="flex-1">
            <Plus className="h-4 w-4 mr-2" />
            Add Rule
          </Button>
          <Button type="button" variant="outline" onClick={handleSwitchToView} className="flex-1">
            <Eye className="h-4 w-4 mr-2" />
            Build From View Instead
          </Button>
        </div>
      )}

      {/* When in view mode, offer to switch back */}
      {isViewMode && (
        <Button type="button" variant="outline" onClick={handleSwitchToFile} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Switch to File-Based Rules
        </Button>
      )}

      {/* Empty State */}
      {rules.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground mb-4">
            No rules defined yet. Choose how to select data for this view.
          </p>
          <div className="flex gap-3 justify-center">
            <Button type="button" onClick={handleAddRule}>
              <Plus className="h-4 w-4 mr-2" />
              Add File Rules
            </Button>
            <Button type="button" variant="secondary" onClick={applyViewSwitch}>
              <Eye className="h-4 w-4 mr-2" />
              Build From View
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
